/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */

import { useCallback, useEffect, useState } from 'react';

/** Space taken by the app's fixed chrome, in CSS pixels. */
export interface ChromeInsets {
  top: number;
  bottom: number;
}

/**
 * Measures the fixed header and footer so a panel can sit exactly between them.
 *
 * The chrome is not a constant: the header and the footer can each be pinned or unpinned (unpinned
 * collapses to zero height), the desktop header is replaced by a shorter mobile bar below 768px, and
 * the footer is hidden entirely on mobile. Hard-coding offsets would leave the panel either overlapping
 * the footer or floating away from it, so the real geometry is measured instead.
 *
 * Recomputed on window resize and whenever one of those elements changes size — which is exactly what
 * pinning does — via a ResizeObserver.
 */
export function useChromeInsets(): ChromeInsets {
  const [insets, setInsets] = useState<ChromeInsets>({ top: 0, bottom: 0 });

  const measure = useCallback(() => {
    // Only elements that are actually laid out count: an unpinned footer has zero height and a hidden
    // header has no box at all, and in both cases the panel should extend into that space.
    const visibleHeight = (selector: string): number => {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (!el) return 0;
      const rect = el.getBoundingClientRect();
      if (rect.height === 0) return 0;
      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') return 0;
      return Math.round(rect.height);
    };

    // The desktop header and the mobile bar never show together, so the larger of the two is the offset.
    const top = Math.max(visibleHeight('.fixed-header'), visibleHeight('.mobile-header'));
    const bottom = visibleHeight('.fixed-footer');

    setInsets((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
  }, []);

  useEffect(() => {
    measure();

    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);

    const sizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measure()) : null;

    // Observed elements are re-resolved rather than captured once: the chrome can mount after this hook
    // (a different subtree order) or be swapped at the mobile breakpoint, and a set captured at mount
    // would then never fire, leaving the panel permanently at zero insets.
    const attach = () => {
      sizeObserver?.disconnect();
      ['.fixed-header', '.mobile-header', '.fixed-footer'].forEach((selector) => {
        const el = document.querySelector(selector);
        if (el && sizeObserver) sizeObserver.observe(el);
      });
      measure();
    };
    attach();

    // Pinning toggles a class on the existing bar (height → 0), which ResizeObserver catches on its own;
    // this watches the coarser case of a bar being added or removed from the tree.
    const treeObserver =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver(attach)
        : null;
    treeObserver?.observe(document.body, {
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      sizeObserver?.disconnect();
      treeObserver?.disconnect();
    };
  }, [measure]);

  return insets;
}

export default useChromeInsets;
